import TimeSignature from 'Models/TimeSignature';
import { defaultCountInBeats } from 'Models/CountIn';
import type EventStream from 'Models/EventStream';
import type ITimeKeeper from 'Models/ITimeKeeper';
import {
  beatGridOf,
  beatIndexAtOrBefore,
  beatStartSecs,
  isPlayableTimeSignature,
} from 'Models/MeterTiming';
import type { CountInRequest, PlayAlongLoop } from 'Utils/Scheduler';

/**
 * The count-in's inputs for ungraded play (Play Along), read by the sight
 * reading and repertoire scenes each time playback starts.
 *
 * `ungraded` is an explicit input: the play surfaces set it from the
 * player's effective grading (`useCountInGradingInput`). A development-only
 * URL switch can also turn it on (see `isDevUngradedSwitchOn`); a production
 * build never reads the switch.
 *
 * Another surface that must not wait for a first note (an exercise the app
 * cannot hear, say) uses the same engine: before it starts its transport,
 * `scheduler.requestCountIn(countInRequestFor({ ungraded: true, timeKeeper,
 * eventStream }))`, then it unpauses as usual; `CountInCursorHold` keeps its
 * cursor on the first note through the count-in. A resume after a long pause
 * moves the resume position back with `countInJumpBackMeasure` and then plays:
 * every ungraded start counts in on its own.
 */

/**
 * Build switch: the continuous loop count-in, where the beat never breaks
 * across the loop wrap and the count-in beats sit in the grid. When off, an
 * ungraded loop stops at its end and a count-in after a short lead brings it
 * back in (the simple loop).
 *
 * Off until the continuous loop is proven on iOS and Android devices. Even when
 * on it is web-only: the native schedulers always run the simple loop.
 */
export const CONTINUOUS_LOOP_COUNT_IN = false;

const DEV_UNGRADED_PARAM = 'PLAY_ALONG_UNGRADED';
const DEV_CONTINUOUS_LOOP_PARAM = 'PLAY_ALONG_CONTINUOUS_LOOP';

/** True in a production build, where the development switch does not exist. */
const isProductionBuild = (): boolean => process.env.REACT_APP_NODE_ENV === 'prd';

const devParam = (name: string): string | null => {
  if (isProductionBuild() || typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
};

/**
 * Development only: `?PLAY_ALONG_UNGRADED=true` plays ungraded with the
 * count-in. Always false in a production build.
 */
export const isDevUngradedSwitchOn = (): boolean => devParam(DEV_UNGRADED_PARAM) === 'true';

/** Development only: `?PLAY_ALONG_CONTINUOUS_LOOP=false|true` overrides the build switch. */
const continuousLoopCountIn = (): boolean => {
  const override = devParam(DEV_CONTINUOUS_LOOP_PARAM);
  return override === null ? CONTINUOUS_LOOP_COUNT_IN : override === 'true';
};

let ungradedInput: boolean | null = null;

/**
 * Set from the player's effective grading by the play surfaces
 * (`useCountInGradingInput`); null when no surface is mounted.
 */
export const setPlayAlongCountInUngraded = (ungraded: boolean | null): void => {
  ungradedInput = ungraded;
};

/**
 * Whether playback starting now is ungraded and so counts in on its own: the
 * player is not graded, or (development builds only) the URL switch is on.
 */
export const isPlayAlongCountInUngraded = (): boolean =>
  ungradedInput === true || isDevUngradedSwitchOn();

/** The count-in length for a meter: always one bar (there is no length control). */
export const playAlongCountInBeats = (timeSignature: TimeSignature): number =>
  defaultCountInBeats(timeSignature);

/** The loop settings for ungraded repertoire, or null when graded. */
export const playAlongLoop = (
  ungraded: boolean,
  timeSignature: TimeSignature,
): PlayAlongLoop | null =>
  ungraded
    ? { countInBeats: playAlongCountInBeats(timeSignature), continuous: continuousLoopCountIn() }
    : null;

/** Bars of music searched for the first note after a start position. */
const ENTRY_SEARCH_MEASURES = 64;

/**
 * The first note at or after a start position, in transport measures: the
 * count-in leads into that note. A start with no note after it counts into
 * the start itself.
 */
export const firstNoteMeasureAt =
  (eventStream: Pick<EventStream, 'getPlaybackNoteEventsBetween'>, wholeNotesPerMeasure: number) =>
  (startMeasure: number): number => {
    if (!(wholeNotesPerMeasure > 0)) return startMeasure;
    const startWholeNotes = startMeasure * wholeNotesPerMeasure;
    // getPlaybackNoteEventsBetween excludes its start, so open it a hair early.
    const notes = eventStream.getPlaybackNoteEventsBetween(
      startWholeNotes - 1e-6,
      startWholeNotes + ENTRY_SEARCH_MEASURES * wholeNotesPerMeasure,
    );
    const firstNote = notes[0];
    return firstNote === undefined
      ? startMeasure
      : Math.max(startMeasure, firstNote.timestamp / wholeNotesPerMeasure);
  };

/**
 * The count-in request for a start on `timeKeeper`'s meter, leading into the
 * first note of `eventStream` after the start. Null when the start is graded
 * (graded play waits for the player's first note instead) or the meter is not
 * playable yet.
 */
export const countInRequestFor = ({
  ungraded,
  timeKeeper,
  eventStream,
  continuesLoop = false,
}: {
  ungraded: boolean;
  timeKeeper: ITimeKeeper;
  eventStream: Pick<EventStream, 'getPlaybackNoteEventsBetween'>;
  continuesLoop?: boolean;
}): CountInRequest | null => {
  const timeSignature = timeKeeper.getTimeSignature();
  if (!ungraded || !isPlayableTimeSignature(timeSignature)) return null;
  return {
    beats: playAlongCountInBeats(timeSignature),
    entryMeasureAt: firstNoteMeasureAt(eventStream, timeKeeper.measureDurationAt(0)),
    continuesLoop: continuesLoop && continuousLoopCountIn(),
  };
};

/**
 * Where the running transport is now on its beat grid, in grid bars (bar 0
 * starts on beat 0, a downbeat; a pickup shifts the piece by
 * `pickupMeasures`), read from the audio clock. Null while it is paused.
 */
export const transportGridBarsNow = (
  timeKeeper: ITimeKeeper,
  pickupMeasures: number,
): number | null => {
  const measureSecs = timeKeeper.getMeasureDurationSecs();
  if (timeKeeper.isPaused() || !(measureSecs > 0)) return null;
  const ticksPerMeasure = timeKeeper.getTicksPerMeasure();
  const startMeasure =
    ticksPerMeasure > 0 ? timeKeeper.getPlaybackStartTicks() / ticksPerMeasure : 0;
  const gridOrigin =
    timeKeeper.getPlaybackStartTime() - (startMeasure + pickupMeasures) * measureSecs;
  return (timeKeeper.getAudioContextTime() - gridOrigin) / measureSecs;
};

/**
 * The beat of the transport's beat grid sounding now while it counts in
 * (beat 0 is a downbeat), with when it started; null when the transport is
 * paused or not counting in. Scenes pulse the waiting cursor when it changes.
 */
export const countInBeatNow = (
  timeKeeper: ITimeKeeper,
  pickupMeasures: number,
): { beatIndex: number; startedSecsAgo: number } | null => {
  const timeSignature = timeKeeper.getTimeSignature();
  const nowBars = transportGridBarsNow(timeKeeper, pickupMeasures);
  if (
    nowBars === null ||
    !timeKeeper.getIsCountingIn() ||
    !isPlayableTimeSignature(timeSignature)
  ) {
    return null;
  }
  const grid = beatGridOf(timeSignature);
  const measureSecs = timeKeeper.getMeasureDurationSecs();
  const elapsed = nowBars * measureSecs;
  const beatIndex = beatIndexAtOrBefore(elapsed, measureSecs, grid);
  return { beatIndex, startedSecsAgo: elapsed - beatStartSecs(beatIndex, measureSecs, grid) };
};
